import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import ScrollHintRow from '../ScrollHintRow/ScrollHintRow';

export const defaultFilters = {
    orientation: 'all',
    minWidth: 0,
    colors: '',
    order: 'popular',
    safesearch: false,
};

const COLORS = [
    'grayscale', 'transparent', 'red', 'orange', 'yellow', 'green',
    'turquoise', 'blue', 'lilac', 'pink', 'white', 'gray', 'black', 'brown',
];

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Pill-styled select with a custom chevron so the arrow never hugs the border.
// `label` is the select's accessible name — the visible text is the *selected
// option*, which doesn't say what the control does, so screen readers need it.
const SelectField = ({ label, value, onChange, children }) => (
    <div className="relative shrink-0">
        <select
            aria-label={label}
            value={value}
            onChange={onChange}
            className="appearance-none min-h-[44px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 rounded-full pl-3.5 pr-9 py-2 focus-visible:ring-2 focus-visible:ring-brand-400 cursor-pointer"
        >
            {children}
        </select>
        <FontAwesomeIcon
            icon={faChevronDown}
            className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500"
        />
    </div>
);

const Filters = ({ filters, setFilters, showColor = true, showOrientation = true }) => {
    const update = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

    return (
        // Inline row; scrolls horizontally when the pills don't fit (with edge
        // fades to hint the overflow) instead of wrapping and eating height. On
        // wide screens the flex-1 search box pushes these to the right with no
        // overflow, so no fade shows.
        <ScrollHintRow className="items-center gap-2 py-0.5">
            {showOrientation && (
                <SelectField
                    label="Orientation"
                    value={filters.orientation}
                    onChange={(e) => update('orientation', e.target.value)}
                >
                    <option value="all">Orientation: Any</option>
                    <option value="horizontal">Horizontal</option>
                    <option value="vertical">Vertical</option>
                </SelectField>
            )}

            <SelectField
                label="Minimum size"
                value={filters.minWidth}
                onChange={(e) => update('minWidth', Number(e.target.value))}
            >
                <option value={0}>Size: Any</option>
                <option value={1280}>Large (1280px+)</option>
                <option value={1920}>Full HD (1920px+)</option>
                <option value={3840}>4K (3840px+)</option>
            </SelectField>

            {showColor && (
                <SelectField
                    label="Colour"
                    value={filters.colors}
                    onChange={(e) => update('colors', e.target.value)}
                >
                    <option value="">Color: Any</option>
                    {COLORS.map((c) => (
                        <option key={c} value={c}>{cap(c)}</option>
                    ))}
                </SelectField>
            )}

            <SelectField
                label="Sort order"
                value={filters.order}
                onChange={(e) => update('order', e.target.value)}
            >
                <option value="popular">Published: Popular</option>
                <option value="latest">Latest</option>
            </SelectField>

            <label className="inline-flex items-center gap-2 min-h-[44px] text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer select-none px-2 shrink-0">
                {/* The box stays 16px; the tap-target wrapper gives it a 44×44
                    hit area, and a click anywhere on it still toggles via the
                    surrounding <label>. */}
                <span className="tap-target inline-flex h-4 w-4 shrink-0">
                    <input
                        type="checkbox"
                        checked={filters.safesearch}
                        onChange={(e) => update('safesearch', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 accent-brand-500 cursor-pointer"
                    />
                </span>
                SafeSearch
            </label>
        </ScrollHintRow>
    );
};

export default Filters;
